import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSex as updateSex } from "../../redux/userSlice";
import {
    Box,
    FormControl,
    Chip,
    Stack,
    TextField,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

const contraceptiveOptions = [
    "Not using contraception",
    "Combined birth control pill",
    "Progestin-only pill (mini pill)",
    "Hormonal IUD (Mirena, Kyleena, etc.)",
    "Copper IUD (non-hormonal)",
    "Hysterectomy",
    "Implant (Nexplanon)",
    "Injection (Depo-Provera)",
    "Vaginal ring (NuvaRing)",
    "Patch (Xulane)",
    "Tubal ligation or partner has vasectomy",
    "Condoms or other barrier methods only",
    "My contraceptive is not listed",
];

export default function ContraceptiveStatusChips() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

    const user = useSelector((state) => state.user);

    const [selectedContraceptives, setSelectedContraceptives] = useState(
        Array.isArray(user.sex?.contraceptive) ? user.sex.contraceptive : []
    );
    const [otherContraceptive, setOtherContraceptive] = useState(
        user.sex?.otherContraceptive || ""
    );

    useEffect(() => {
        if (Array.isArray(user.sex?.contraceptive)) {
            setSelectedContraceptives(user.sex.contraceptive);
        }
        if (user.sex?.otherContraceptive) {
            setOtherContraceptive(user.sex.otherContraceptive);
        }
    }, [user.sex?.contraceptive, user.sex?.otherContraceptive]);

    const toggle = (label) => {
        setSelectedContraceptives((prev) => {
            const isSelected = prev.includes(label);
            let next = isSelected ? prev.filter((x) => x !== label) : [...prev, label];

            // rule: selecting "Not using contraception" clears all others
            if (!isSelected && label === "Not using contraception") {
                next = ["Not using contraception"];
            }

            // rule: selecting anything else removes "Not using contraception"
            if (label !== "Not using contraception" && !isSelected) {
                next = next.filter((x) => x !== "Not using contraception");
            }

            // if "My contraceptive is not listed" is not selected, clear free text
            if (!next.includes("My contraceptive is not listed")) {
                setOtherContraceptive("");
            }

            return next;
        });
    };

    const handleNext = () => {
        const finalSelection =
            selectedContraceptives.length === 0
                ? ["Not using contraception"]
                : selectedContraceptives;

        dispatch(
            updateSex({
                ...user.sex,
                contraceptive: finalSelection,
                otherContraceptive: otherContraceptive.trim() || undefined,
            })
        );

        setPageAndDeferNavigate(dispatch, "/maternity", navigate);
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, "/menstrual", navigate);
    };

    const showOther = selectedContraceptives.includes("My contraceptive is not listed");

    return (
        <Question
            onBack={handleBack}
            title="What is your current contraceptive status?"
            subtitle="Select one or more, or choose 'Not using contraception'"
            input={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormControl sx={{ width: "100%", mb: "1rem" }}>
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: isDesktop ? "800px" : "100%",
                                mx: "auto",
                                mt: 2,
                            }}
                        >
                            <Stack direction="row" flexWrap="wrap" gap={1.5}>
                                {contraceptiveOptions.map((label) => {
                                    const isSelected = selectedContraceptives.includes(label);

                                    return (
                                        <Chip
                                            key={label}
                                            label={label}
                                            clickable
                                            onClick={() => toggle(label)}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{
                                                fontSize: "0.85rem",
                                                fontWeight: isSelected ? 600 : 400,
                                                backgroundColor: isSelected ? "#82B1FF33" : "transparent",
                                                color: isSelected
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                                borderColor: isSelected
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[400],
                                                height: "auto",
                                                alignItems: "flex-start",
                                                maxWidth: isDesktop ? "380px" : "100%",

                                                "& .MuiChip-label": {
                                                    display: "block",
                                                    whiteSpace: "normal",
                                                    overflowWrap: "anywhere",
                                                    lineHeight: 1.2,
                                                    paddingTop: "6px",
                                                    paddingBottom: "6px",
                                                },

                                                "&:hover": {
                                                    backgroundColor: isSelected
                                                        ? "#82B1FF33"
                                                        : theme.palette.action.hover,
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Stack>

                            {showOther && (
                                <Box sx={{ mt: 3 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        color="secondary"
                                        label="Please specify your contraceptive"
                                        value={otherContraceptive}
                                        onChange={(e) => setOtherContraceptive(e.target.value)}
                                        sx={{
                                            ...textAreaSx,
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        width: "100%",
                        maxWidth: "900px",
                        margin: "0 auto",
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title={
                            selectedContraceptives.length === 0
                                ? "Not using contraception"
                                : "Continue"
                        }
                        disabled={false}
                        selected={selectedContraceptives.length !== 0}
                    />
                </Box>
            }
        />
    );
}
